import { useMEbix } from "@/hooks/use-ebix";
import useUserInfo from "@/hooks/use-user-info";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import PasswordInput from "../input-password";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { IFeatureListResponseData } from "@/model/feature-list";

const FeatureListItemLin = ({
  keyValue,
  feature,
}: {
  keyValue: number;
  feature: IFeatureListResponseData;
}) => {
  const userInfo = useUserInfo();
  const ebixMQuery = useMEbix();

  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState("");

  const loadLinModule = () => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = ebixMQuery.data.SAMLUrl ?? "";
    form.target = "_blank"; // Open in a new tab

    // Create a hidden input for SAMLResponse
    const samlInput = document.createElement("input");
    samlInput.type = "hidden";
    samlInput.name = "SAMLResponse";
    samlInput.value = ebixMQuery.data.SAMLResponse ?? "";
    form.appendChild(samlInput);

    // Create a hidden input for RelayState (if needed)
    const relayStateInput = document.createElement("input");
    relayStateInput.type = "hidden";
    relayStateInput.name = "RelayState";
    relayStateInput.value = ""; // Set your relay state value if needed
    form.appendChild(relayStateInput);

    // Append the form to the body

    document.body.appendChild(form);

    // Submit the form
    form.submit();

    // Remove the form from the DOM
    document.body.removeChild(form);
  };

  const handleFeatureClick = () => setShowPasswordDialog(!showPasswordDialog);

  const handleDialogPasswordSubmit = () => {
    if (password) {
      ebixMQuery.mutate(
        {
          info: userInfo,
          password,
          resource: feature.tooltip,
        },
        {
          onError(error) {
            console.error(error);
          },
        },
      );

      setShowPasswordDialog(false);
      setPassword("");
    }
  };

  useEffect(() => {
    if (ebixMQuery.isSuccess && Boolean(ebixMQuery.data?.summary)) {
      loadLinModule();
    }
    // else if (
    //   // ebixMQuery.data?.summary &&
    //   ebixMQuery.data?.summary?.status_message === "failed"
    // ) {
    //   emitEvent(EventConstants.ADD_ERROR, {
    //     message: ebixMQuery.data?.summary?.message,
    //     type: ErrorNotificationType.ERROR,
    //   });
    //   console.log("ebix data: ", ebixMQuery.data?.summary);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ebixMQuery.isSuccess, ebixMQuery.data]);

  return (
    <>
      {/* 2.1.1 Keyboard — A / 4.1.2 — A: was a <Card onClick>, i.e. a non-focusable
          <div>. See feature-list-item.tsx for the full note. */}
      <button
        type="button"
        key={keyValue}
        onClick={() => handleFeatureClick()}
        /*
          NOTE: the `order-[ … ${feature.order}]` template literal is malformed in the
          original source — the interpolation sits at the end of the string instead of
          inside `order-[…]`, so the order utility never applies and the trailing class is
          corrupted. That pre-existing bug is documented in docs/improvements/ and is
          deliberately left intact here: repairing it would suddenly start applying CSS
          `order` and reshuffle the home tiles, which is a visual change outside this
          audit's scope. The focus-visible classes are appended AFTER the corrupted token
          so the focus ring itself is valid.
        */
        className={`add-class order-[ flex h-[110px] w-[110px] cursor-pointer flex-col items-center justify-start gap-4 rounded-xl border-0 bg-card p-2 pt-5 text-card-foreground shadow-md hover:bg-primary-extraLight${feature.order}] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
      >
        {ebixMQuery.isPending ? (
          <Loader2
            aria-hidden="true"
            className="size-10 animate-spin text-blue-500 motion-reduce:animate-none"
          />
        ) : (
          <div className="aspect-square h-8">
            <img
              src={`data:image/svg+xml;base64,${feature.icon}`}
              alt=""
              className="h-full object-contain"
            />
          </div>
        )}

        <span className="overflow-hidden text-ellipsis text-center text-sm font-medium leading-none">
          {feature.name}
        </span>
      </button>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="flex flex-col gap-10">
          {/*
            4.1.2 Name, Role, Value — A. This was a plain <h2>, so the dialog had no
            accessible name — a screen reader announced "dialog" and nothing else, and
            Radix logs a warning. DialogTitle supplies the name and is still a heading.
          */}
          <DialogTitle className="text-lg font-bold">
            Enter Password to access LIN Module
          </DialogTitle>
          <div className="flex flex-col gap-2">
            <PasswordInput
              data-testid="password"
              id="password"
              // 3.3.2 Labels or Instructions — A: the label was commented out, leaving the
              // placeholder as the only cue.
              label="Password"
              placeholder="Password"
              value={password}
              onChange={setPassword}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleDialogPasswordSubmit} disabled={!password}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeatureListItemLin;
